import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getRentaDashboard, getAsesores } from '../../../js/rentaService';
import RentaFilters from '../../../components/ui/Admin/CarteraComponents/RentaFilters';
import RentaKPICards from '../../../components/ui/Admin/CarteraComponents/RentaKPICards';
import RentaTable from '../../../components/ui/Admin/CarteraComponents/RentaTable';
import RentaBarChart from '../../../components/ui/Admin/CarteraComponents/FormComponents/RentaBarChart';
import RentaPieChart from '../../../components/ui/Admin/CarteraComponents/FormComponents/RentaPieChart';
import ReporteRentaExcel from '../../../components/ui/Admin/CarteraComponents/ReporteRentaExcel';
import {
  SkeletonLoansTable,
  SkeletonKPICard,
  SkeletonChart,
  SkeletonFilters,
  EmptyState,
} from '../../../components/ui/Admin/DashboardComponents/LoadingSkeletons/SkeletonKPICard';
import FetchWithGif from '../../../components/Reutilizables/FetchWithGif';

const HORARIO_PAGO_LABELS = {
  '07_11': '7:00:00 - 11:00:00',
  '11_15': '11:00:01 - 15:00:00',
  '15_18': '15:00:01 - 18:00:00',
  '18_19': '18:00:01 - 19:00:00',
  '19_24': '19:00:01 - 23:59:59',
};

export default function DashboardRentaPage() {
  const [filters, setFilters] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    advisor: '',
    horario_pago: '',
    abonado_por: '',
    page: 1,
  });

  const [asesores, setAsesores] = useState([]);
  const [uiErrors, setUiErrors] = useState({});
  const [serverErrorMsg, setServerErrorMsg] = useState('');

  const [state, setState] = useState({
    loading: false,
    error: '',
    data: [],
    meta: {},
    charts: {},
    initialLoad: true,
  });

  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const excelDataRef = useRef({ cuotas: [], meta: {}, isGenerating: false });
  const abortRef = useRef(null);
  const requestIdRef = useRef(0);
  
  const validateFilters = (f) => {
    const errors = {};
    const { fecha_inicio, fecha_fin } = f;
    const isISO = (s) => /\d{4}-\d{2}-\d{2}/.test(s);

    if (!fecha_inicio) {
      errors.fecha_inicio = 'La fecha de inicio es obligatoria.';
    } else if (!isISO(fecha_inicio)) {
      errors.fecha_inicio = 'Formato inválido. Use AAAA-MM-DD.';
    }

    if (!fecha_fin) {
      errors.fecha_fin = 'La fecha de fin es obligatoria.';
    } else if (!isISO(fecha_fin)) {
      errors.fecha_fin = 'Formato inválido. Use AAAA-MM-DD.';
    }

    if (fecha_inicio && fecha_fin && isISO(fecha_inicio) && isISO(fecha_fin)) {
      const start = new Date(fecha_inicio);
      const end = new Date(fecha_fin);
      if (end < start) {
        errors.fecha_fin = 'La fecha de fin debe ser posterior o igual a la fecha de inicio.';
      }
    }

    return errors;
  };

  useEffect(() => {
    const loadAsesores = async () => {
      try {
        const result = await getAsesores();
        const rawList = Array.isArray(result?.asesores)
          ? result.asesores
          : Array.isArray(result?.data)
            ? result.data
            : Array.isArray(result)
              ? result
              : [];

        const normalized = rawList.map((item) => {
          const id =
            item.id ??
            item.idUsuario ??
            item.idAsesor ??
            item.asesor_id ??
            item.user_id ??
            null;
          const nombre =
            item.nombre_completo ||
            item.nombre ||
            [item.nombre, item.apellidoPaterno, item.apellidoMaterno]
              .filter(Boolean)
              .join(' ')
              .trim() ||
            (id != null ? `Asesor #${id}` : 'Asesor');

        return {
            id,
            nombre_completo: nombre,
          };
        });

        setAsesores(normalized);
      } catch (e) {
        console.error('Error al cargar asesores:', e);
      }
    };
    loadAsesores();
  }, []);

  const fetchData = useCallback(async (isInitialLoad = false) => {
    const requestId = ++requestIdRef.current;
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setState((s) => ({ ...s, loading: true, initialLoad: isInitialLoad }));
    setServerErrorMsg('');

    const errors = validateFilters(filters);
    if (Object.keys(errors).length > 0) {
      setUiErrors(errors);
      setState((s) => ({ ...s, loading: false, initialLoad: false }));
      return;
    }

    try {
      const result = await getRentaDashboard(filters, { signal: controller.signal });
      if (requestId !== requestIdRef.current) return;
      
      setState({
        loading: false,
        error: '',
        data: Array.isArray(result?.data) ? result.data : [],
        meta: result?.meta ?? {},
        charts: result?.charts ?? {},
        initialLoad: false,
      });
      setUiErrors({});
    } catch (e) {
      if (e?.name === 'AbortError') {
        if (requestId === requestIdRef.current) {
          setState((s) => ({ ...s, loading: false, initialLoad: false }));
        }
        return;
      }

      if (requestId !== requestIdRef.current) return;
      const status = e?.status || e?.response?.status;
      const payload = e?.response?.data || {};

      if (status === 422) {
        const mapped = {};
        const backendErrors = payload?.errors || {};
        
        Object.keys(backendErrors).forEach(key => {
          if (backendErrors[key]?.length) {
            mapped[key] = backendErrors[key][0];
          }
        });
        
        setUiErrors(mapped);
        setServerErrorMsg(payload?.message || 'Revise los campos marcados.');
        setState((s) => ({ ...s, loading: false, initialLoad: false }));
        return;
      }

      setState((s) => ({
        ...s,
        loading: false,
        error: e?.message || 'No se pudo cargar el dashboard de renta.',
        initialLoad: false,
      }));
    }
  }, [filters]);

  useEffect(() => {
    if (!state.initialLoad) {
      fetchData(false);
    }
  }, [filters.page]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
    setUiErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const onApply = () => {
    const errors = validateFilters(filters);
    setUiErrors(errors);
    if (Object.keys(errors).length === 0) {
      setUiErrors({});
      setServerErrorMsg('');
      fetchData(true);
    } else {
      setServerErrorMsg('Por favor, corrija los errores en las fechas.');
    }
  };

  const onResetToday = () => {
    setFilters({ 
      fecha_inicio: '', 
      fecha_fin: '',
      advisor: '',
      horario_pago: '',
      abonado_por: '',
      page: 1,
    });
    setUiErrors({});
    setServerErrorMsg('');
  };

  const handlePageChange = useCallback((newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleExportExcel = useCallback(async () => {
    if (excelDataRef.current.isGenerating) return;
    
    const errors = validateFilters(filters);
    if (Object.keys(errors).length > 0) {
      setUiErrors(errors);
      setServerErrorMsg('Por favor, corrija los errores en las fechas antes de exportar.');
      return;
    }

    setIsLoadingExport(true);
    excelDataRef.current.isGenerating = true;

    try {
      const allDataFilters = {
        ...filters,
        get_all: true,
      };

      const result = await getRentaDashboard(allDataFilters);
      
      // Guardar en ref (NO causa re-render): almacena todos los datos para exportar sin forzar actualización del componente
      if (result?.data && result.data.length > 0) {
        excelDataRef.current = {
          cuotas: result.data,
          meta: result.meta,
          isGenerating: true,
        };
      } else {
        excelDataRef.current = { cuotas: [], meta: {}, isGenerating: false };
      }
      setIsLoadingExport(false);
    } catch (e) {
      console.error('Error al obtener datos para Excel:', e);
      excelDataRef.current.isGenerating = false;
      setIsLoadingExport(false);
    }
  }, [filters]);

  const handleExcelComplete = useCallback(() => {
    excelDataRef.current = { cuotas: [], meta: {}, isGenerating: false };
  }, []);

  const handleExcelGenerated = useCallback((url) => {
    const link = document.createElement('a');
    link.href = url;
    const fileName = `Reporte_Renta_${filters.fecha_inicio}_${filters.fecha_fin}.xlsx`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [filters.fecha_inicio, filters.fecha_fin]);

  const meta = useMemo(() => state?.meta ?? {}, [state?.meta]);
  const data = useMemo(() => state?.data ?? [], [state?.data]);
  const charts = state?.charts ?? {};
  const chartsByAdvisor = charts?.by_advisor;

  const kpiData = useMemo(
    () => ({
      total_amortizacion: meta?.total_general_amortizacion ?? 0,
      total_renta: meta?.total_general_renta ?? 0,
      total_cuotas: meta?.total_general_cuotas ?? 0,
      promedio_amortizacion: meta?.promedio_amortizacion ?? 0,
      promedio_renta: meta?.promedio_renta ?? 0,
      asesores_activos: meta?.asesores_activos ?? 0,
    }),
    [meta]
  );

  const chartRows = useMemo(() => {
    const source = Array.isArray(chartsByAdvisor) ? chartsByAdvisor : null;
    if (source && source.length > 0) {
      return source
        .map((row) => {
          const name =
            row?.asesor_nombre ||
            (row?.asesor_id != null ? `Asesor ${row.asesor_id}` : 'Asesor');
          return {
            asesor: String(name),
            amortizacion: Math.round(Number(row?.amortizacion ?? 0) * 100) / 100,
            renta: Math.round(Number(row?.renta ?? 0) * 100) / 100,
            cuotas: Number(row?.cuotas ?? 0),
          };
        })
        .sort((a, b) => b.amortizacion - a.amortizacion);
    }

    const grouped = {};
    
    data.forEach(item => {
      const asesorId = item.asesor?.id;
      const asesorNombre = item.asesor?.nombre || `Asesor ${asesorId}`;
      
      if (!grouped[asesorId]) {
        grouped[asesorId] = {
          asesor: asesorNombre,
          amortizacion: 0,
          renta: 0,
          cuotas: 0,
        };
      }
      
      grouped[asesorId].amortizacion += Number(item.capital) || 0;
      grouped[asesorId].renta += Number(item.interes) || 0;
      grouped[asesorId].cuotas += 1;
    });

    return Object.values(grouped).sort((a, b) => b.amortizacion - a.amortizacion);
  }, [chartsByAdvisor, data]);

  const barChartData = useMemo(() => {
    return chartRows.slice(0, 10);
  }, [chartRows]);

  const pieChartData = useMemo(() => {
    return chartRows
      .slice(0, 8)
      .map(item => ({
        name: item.asesor,
        value: item.renta,
      }));
  }, [chartRows]);

  const showSkeleton = state.loading && state.initialLoad && data.length === 0;
  
  if (showSkeleton) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
          <header className="mb-6">
            <div className="h-8 w-80 bg-gray-200 rounded mb-2 animate-pulse"></div>
            <div className="h-4 w-60 bg-gray-200 rounded animate-pulse"></div>
          </header>

          <SkeletonFilters />

          <section className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonKPICard key={i} />
            ))}
          </section>

          <section className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SkeletonChart title="Cargando gráfico..." />
            <SkeletonChart title="Cargando gráfico..." />
          </section>

          <SkeletonLoansTable />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {state.loading && !state.initialLoad && <FetchWithGif />}
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto space-y-6">

        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-l-4 border-red-500 p-6 md:p-7">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-red-500">Renta y amortización</p>
            <h1 className="mt-2 text-2xl font-bold text-slate-900">Seguimiento de cuotas canceladas</h1>
            <p className="mt-3 max-w-2xl text-sm text-slate-600">
              Controla recuperación de capital, rentabilidad y aporte por asesor dentro del rango seleccionado.
            </p>
          </div>
        </section>

        <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="mb-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Filtros de periodo</p>
            <h2 className="mt-1 text-lg font-semibold text-slate-900">Segmentación de cobranza</h2>
          </div>
          <RentaFilters
            filters={filters}
            onChange={onChange}
            onApply={onApply}
            onReset={onResetToday}
            loading={state.loading}
            errors={uiErrors}
            formError={serverErrorMsg}
            asesores={asesores}
          />
        </section>

        <RentaKPICards totals={kpiData} />

        {data.length > 0 && (
          <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
              <RentaBarChart 
                data={barChartData} 
                title="Amortización vs Renta"
                loading={state.loading} 
              />
            </div>
            <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
              <RentaPieChart 
                data={pieChartData} 
                title="Distribución de Renta por Asesor"
                loading={state.loading} 
              />
            </div>
          </section>
        )}

        {data.length === 0 && !state.loading ? (
          <div className="rounded-3xl border border-dashed border-gray-200 bg-white p-8 shadow-sm">
            <EmptyState
              title="No hay datos de cuotas canceladas"
              description="No se encontraron cuotas canceladas para los filtros seleccionados"
            />
          </div>
        ) : (
          <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
            <div className="mb-4 flex justify-end">
              <button
                onClick={handleExportExcel}
                disabled={isLoadingExport || state.loading || data.length === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                  isLoadingExport || state.loading || data.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg'
                }`}
              >
                {isLoadingExport ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Generando Excel...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Generar Excel
                  </>
                )}
              </button>
            </div>

            <RentaTable
              rows={data}
              loading={state.loading}
              error={state.error}
              pagination={meta.current_page ? {
                current_page: meta.current_page,
                per_page: meta.per_page,
                last_page: meta.last_page,
                from: meta.from,
                to: meta.to,
                total: meta.total_registros,
              } : null}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {meta.fecha_inicio && meta.fecha_fin && data.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-gray-700">
            <span className="rounded-full bg-gray-100 px-3 py-1 font-medium">
              {meta.fecha_inicio} → {meta.fecha_fin}
            </span>
            {meta.total_registros > 0 && (
              <span className="rounded-full bg-rose-50 px-3 py-1 font-medium text-rose-700">
                {meta.total_registros} cuota{meta.total_registros !== 1 ? 's' : ''} cancelada{meta.total_registros !== 1 ? 's' : ''}
              </span>
            )}
            {filters.advisor && asesores.length > 0 && (
              <span className="rounded-full bg-gray-100 px-3 py-1 font-medium">
                Asesor: {asesores.find(a => String(a.id) === String(filters.advisor))?.nombre_completo || filters.advisor}
              </span>
            )}
            {filters.horario_pago && (
              <span className="rounded-full bg-gray-100 px-3 py-1 font-medium">
                Horario: {HORARIO_PAGO_LABELS[filters.horario_pago] || filters.horario_pago}
              </span>
            )}
            {filters.abonado_por && (
              <span className="rounded-full bg-gray-100 px-3 py-1 font-medium">
                Abonado por: {filters.abonado_por}
              </span>
            )}
          </div>
        )}

        {state.error && data.length > 0 && (
          <div className="fixed bottom-4 right-4 z-50 max-w-sm">
            <div className="bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium">Error al actualizar</p>
                <p className="text-xs opacity-90">{state.error}</p>
              </div>
              <button
                onClick={() => setState((s) => ({ ...s, error: '' }))}
                className="text-white hover:bg-red-700 rounded p-1"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {excelDataRef.current.isGenerating && (
          <ReporteRentaExcel
            cuotas={excelDataRef.current.cuotas}
            filters={filters}
            asesores={asesores}
            meta={excelDataRef.current.meta}
            onComplete={handleExcelComplete}
            onExcelGenerated={handleExcelGenerated}
          />
        )}
      </div>
    </div>
  );
}
